import { vec2 } from 'gl-matrix';
import { defineMessages } from 'react-intl-next';

/**
 * These are shapes that exist from the ShapeTool - that is, in the shape picker of our
 * main toolbar bottom of screen.
 */
export enum ShapeToolShape {
  Rectangle = 1,
  Ellipse = 2,
  RoundedRectangle = 3,
  Diamond = 4,
  UpTriangle = 5,
  DownTriangle = 6,
  LeftParallelogram = 7,
  RightParallelogram = 8,
}

/**
 * These are shapes that do not exist in the ShapeTool.
 * For example, stickies, images, text, stickers all use the SharpRectangle
 * shape in their `ShapeComponent` in their ECS creators.
 */
export enum OtherShapes {
  SharpRectangle = 0,
}

export type ShapesType = ShapeToolShape | OtherShapes;

/**
 * As above, if we want to represent all the shapes that can be used in creating
 * a shape entity, we need an object which combines the shapes from the
 * ShapeTool, and also any of the other shapes.
 */
export const Shapes = {
  ...OtherShapes,
  ...ShapeToolShape,
};

export type RoundedPolygonShape = Exclude<ShapeToolShape, ShapeToolShape.Ellipse>;

/**
 * For WDF, Miro importing or sprites used for shapes in our main toolbar, a
 * well-defined string map between a shape's enum value and its string
 * identifier needs to be made. The following provides this mapping for just
 * `ShapeTool` objects for contexts such as Miro importing where we only
 * translate to `ShapeToolShapes` and do not use `OtherShapes`.
 */
export const shapeToolsToString = {
  [Shapes.Rectangle]: 'rectangle',
  [Shapes.Ellipse]: 'ellipse',
  [Shapes.RoundedRectangle]: 'rounded-rectangle',
  [Shapes.UpTriangle]: 'triangle',
  [Shapes.DownTriangle]: 'upside-down-triangle',
  [Shapes.Diamond]: 'diamond',
  [Shapes.LeftParallelogram]: 'left-parallelogram',
  [Shapes.RightParallelogram]: 'right-parallelogram',
} as const;

/**
 * For contexts where a mapping between all shapes and their string identifier
 * is needed, the following is used.
 */
export const shapesToString = {
  ...shapeToolsToString,
  [Shapes.SharpRectangle]: 'sharp-rectangle',
} as const;

const shapes = defineMessages({
  'whiteboards.shape.ellipse': {
    id: 'whiteboards.shape.ellipse',
    defaultMessage: 'Ellipse',
    description: 'An ellipse shape, ie an elongated circle',
  },
  'whiteboards.shape.rectangle': {
    id: 'whiteboards.shape.rectangle',
    defaultMessage: 'Rectangle',
    description: 'A rectangle shape',
  },
  'whiteboards.shape.rounded-rectangle': {
    id: 'whiteboards.shape.rounded-rectangle',
    defaultMessage: 'Rounded rectangle',
    description: 'A rectangle shape with rounded corners',
  },
  'whiteboards.shape.triangle': {
    id: 'whiteboards.shape.triangle',
    defaultMessage: 'Triangle',
    description: 'A triangle shape with its point at the top',
  },
  'whiteboards.shape.upside-down-triangle': {
    id: 'whiteboards.shape.upside-down-triangle',
    defaultMessage: 'Upside down triangle',
    description: 'A triangle shape with its point at the bottom',
  },
  'whiteboards.shape.diamond': {
    id: 'whiteboards.shape.diamond',
    defaultMessage: 'Diamond',
    description: 'A diamond shape, ie a square rotated 45 degrees',
  },
  'whiteboards.shape.left-parallellogram': {
    id: 'whiteboards.shape.left-parallellogram',
    defaultMessage: 'Left parallelogram',
    description: 'A parallelogram shape sloping to the left',
  },
  'whiteboards.shape.right-parallellogram': {
    id: 'whiteboards.shape.right-parallellogram',
    defaultMessage: 'Right parallelogram',
    description: 'A parallelogram shape sloping to the right',
  },
});

export const shapesToMessageDescriptor = {
  [Shapes.Ellipse]: shapes['whiteboards.shape.ellipse'],
  [Shapes.Rectangle]: shapes['whiteboards.shape.rectangle'],
  [Shapes.RoundedRectangle]: shapes['whiteboards.shape.rounded-rectangle'],
  [Shapes.UpTriangle]: shapes['whiteboards.shape.triangle'],
  [Shapes.DownTriangle]: shapes['whiteboards.shape.upside-down-triangle'],
  [Shapes.Diamond]: shapes['whiteboards.shape.diamond'],
  [Shapes.LeftParallelogram]: shapes['whiteboards.shape.left-parallellogram'],
  [Shapes.RightParallelogram]: shapes['whiteboards.shape.right-parallellogram'],
} as const;

export type ShapeConversion<A, B = A> = {
  contentToEntity: (input: A) => B;
  entityToContent: (input: B) => A;
};

export interface ShapeConversions {
  padding: vec2;
  offset: ShapeConversion<vec2>;
  size: ShapeConversion<vec2>;
}

/**
 * These are the dimensions (in world coordinates) for a shape as it is when dropped on the board
 */
export const shapeBoardSizes: Record<ShapeToolShape, vec2> = {
  [ShapeToolShape.Ellipse]: vec2.fromValues(160, 160),
  [ShapeToolShape.Rectangle]: vec2.fromValues(160, 160),
  [ShapeToolShape.RoundedRectangle]: vec2.fromValues(160, 160),
  [ShapeToolShape.UpTriangle]: vec2.fromValues(178, 166),
  [ShapeToolShape.DownTriangle]: vec2.fromValues(178, 166),
  [ShapeToolShape.Diamond]: vec2.fromValues(178, 178),
  [ShapeToolShape.RightParallelogram]: vec2.fromValues(166, 141),
  [ShapeToolShape.LeftParallelogram]: vec2.fromValues(166, 141),
};
